<template>
  <div class="radio-group-wrapper">
    <label v-if="label" class="radio-group-label">
      {{ label }}
      <span v-if="required" class="required-indicator" aria-label="requis">*</span>
    </label>
    <div
      :class="['radio-group-options', `variant-${variant}`, { 'radio-group-vertical': vertical }]"
      :style="columns ? { '--radio-columns': columns } : null"
      role="radiogroup"
      :aria-labelledby="ariaLabelledby || undefined"
      :aria-label="ariaLabelledby ? undefined : (ariaLabel || label || undefined)"
    >
      <label
        v-for="opt in options"
        :key="opt.value"
        :class="['radio-option', {
          'is-checked': modelValue === opt.value,
          'is-disabled': disabled || opt.disabled,
          'has-error': error
        }]"
      >
        <input
          type="radio"
          :name="name"
          :value="opt.value"
          :checked="modelValue === opt.value"
          :disabled="disabled || opt.disabled"
          :required="nativeRequired"
          :data-testid="opt.testId"
          class="radio-input"
          @change="handleChange(opt.value)"
        />
        <span class="radio-body">
          <span class="radio-text">
            <i v-if="variant === 'card' && opt.icon" :class="[opt.icon, 'radio-icon']" aria-hidden="true"></i>
            {{ opt.label }}
          </span>
          <!-- La description ne se rend qu'en variante « carte » : sur une
               rangée de boutons radio ordinaires elle ferait deux hauteurs. -->
          <span v-if="variant === 'card' && opt.description" class="radio-description">{{ opt.description }}</span>
        </span>
      </label>
    </div>
    <span v-if="error" class="error-text" role="alert" aria-live="polite">{{ error }}</span>
  </div>
</template>

<script setup>
const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean],
    default: null
  },
  /** `{ value, label, disabled?, testId?, icon?, description? }[]` — `disabled` grise une seule option. */
  options: {
    type: Array,
    required: true
  },
  label: {
    type: String,
    default: null
  },
  /**
   * Nom accessible du groupe quand l'intitulé est rendu par l'appelant (une
   * question au-dessus des options). Déclarés en props pour atteindre
   * l'élément `role="radiogroup"` : en attributs libres, ils retombaient sur
   * l'enveloppe, sans rôle, et le groupe restait sans nom.
   */
  ariaLabelledby: {
    type: String,
    default: null
  },
  ariaLabel: {
    type: String,
    default: null
  },
  name: {
    type: String,
    default: () => `radio-${Math.random().toString(36).substr(2, 9)}`
  },
  required: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: null
  },
  vertical: {
    type: Boolean,
    default: false
  },
  /**
   * `card` : un choix qui se pose en grille, chaque option portant son
   * pictogramme et une phrase qui dit ce qu'elle fait. C'est la forme d'un
   * choix structurant — le mode de diffusion d'une mission, par exemple —
   * là où la forme par défaut convient à un choix court entre deux mots.
   */
  variant: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'card'].includes(value)
  },
  /** Nombre de colonnes de la grille, en variante `card` uniquement. */
  columns: {
    type: Number,
    default: null
  },
  /**
   * Pose `required` sur les boutons radio eux-mêmes : dans un `<form>` validé
   * par le navigateur, l'envoi est alors refusé tant qu'aucune option n'est
   * choisie, au même titre qu'un champ vide. `required` seul ne fait
   * qu'afficher l'astérisque — c'est le comportement historique, que les
   * formulaires qui valident en JavaScript attendent.
   */
  nativeRequired: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const handleChange = (value) => {
  emit('update:modelValue', value)
}
</script>

<style scoped>
.radio-group-wrapper {
  display: flex;
  flex-direction: column;
}

.radio-group-label {
  display: block;
  margin-bottom: var(--spacing-xs);
  font-weight: 500;
  color: var(--color-text-primary);
  font-size: 0.875rem;
}

.required-indicator {
  color: var(--color-danger);
  margin-left: 2px;
}

.radio-group-options {
  display: flex;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

.radio-group-vertical {
  flex-direction: column;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  cursor: pointer;
  user-select: none;
  transition: all var(--transition-base);
  padding: 0.6rem 0.9rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-bg-secondary);
  min-height: 40px;
}

.radio-option:hover:not(.is-disabled) {
  background: var(--color-bg-hover);
}

.radio-option.is-checked {
  background: var(--color-bg-secondary);
  border-color: var(--color-primary);
}

.radio-option.is-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.radio-option.has-error {
  border-color: var(--color-danger);
}

.radio-input {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: var(--color-primary);
  flex-shrink: 0;
  margin: 0;
}

.radio-input:disabled {
  cursor: not-allowed;
}

.radio-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.radio-text {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-2);
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-primary);
  transition: color var(--transition-base);
}

.radio-option.is-checked .radio-text {
  color: var(--color-primary-dark);
}

.radio-option.is-disabled .radio-text {
  color: var(--color-text-secondary);
}

.error-text {
  display: block;
  margin-top: var(--spacing-xs);
  font-size: 0.875rem;
  color: var(--color-danger);
}

/* ── Variante « carte » ────────────────────────────────────────────────────
   Un choix structurant : chaque option occupe une case de grille, porte son
   pictogramme et la phrase qui dit ce qu'elle fait. Le bouton radio reste
   visible et actionnable — c'est lui qui porte le focus au clavier. */
.radio-group-options.variant-card {
  display: grid;
  grid-template-columns: repeat(var(--radio-columns, 3), minmax(0, 1fr));
  gap: var(--spacing-3);
}

.variant-card .radio-option {
  align-items: flex-start;
  padding: var(--spacing-4);
  background: var(--color-bg-primary);
  border-radius: var(--radius-lg);
  min-height: 44px;
}

.variant-card .radio-option:hover:not(.is-disabled) {
  background: var(--color-primary-lightest);
  border-color: var(--color-primary-light);
}

.variant-card .radio-option.is-checked {
  background: var(--color-primary-lightest);
  border-color: var(--color-primary);
}

.variant-card .radio-option:focus-within {
  box-shadow: var(--focus-ring);
}

.variant-card .radio-icon {
  color: var(--color-text-tertiary);
  font-size: var(--font-size-sm);
  line-height: 1;
}

.variant-card .radio-option.is-checked .radio-icon {
  color: var(--color-primary);
}

.radio-description {
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  line-height: 1.4;
}

@media (max-width: 768px) {
  .radio-group-options.variant-card {
    grid-template-columns: 1fr;
  }
}
</style>
